import api from './axios';
import type { AgentStateResponse, AgentTraceResponse } from './types';

export interface GoogleConnectResponse {
    auth_url: string;
    workspace_id: string;
}

export interface GoogleStatusResponse {
    connected: boolean;
    provider_account_email?: string;
    updated_at?: string;
}

export interface ImportResponse {
    job_ids: string[];
    status: string;
}

export interface GoogleSheetsStructuredResponse {
    source_document: Record<string, unknown>;
    active_version_id?: string | null;
    classification: Record<string, unknown> | null;
    normalized_tables: Array<{
        tab_name?: string;
        logical_table_id?: string;
        table_type?: string;
        source_range?: string;
        columns: unknown[];
        row_count: number;
        rows_truncated: boolean;
        rows: unknown[];
    }>;
    snapshots_summary: Array<{
        tab_name?: string;
        source_range?: string;
        header_row_index?: number;
        value_rows_total: number;
        preview_header_row: unknown;
    }>;
    retrieval_chunks: Array<{
        chunk_type?: string;
        tab_name?: string | null;
        logical_table_id?: string | null;
        text: string;
        metadata: Record<string, unknown>;
        pinecone_vector_id?: string;
    }>;
}

export interface JobStatusResponse {
    job_id: string;
    status: string;
    version_id?: string;
    source_document_id?: string;
    warnings?: string[];
    error?: string | null;
    rows_processed?: number;
    tabs_processed?: number;
    completed_at?: string;
    /** inserted = new row; updated = same sheet; restored = previously soft-deleted row brought back. */
    finz_docs_row_action?: 'inserted' | 'updated' | 'restored';
    finz_docs_title?: string;
}

const googleSheetsIntegrationApi = {
    async getConnectUrl(): Promise<GoogleConnectResponse> {
        const res = await api.get<GoogleConnectResponse>('/integrations/google/connect');
        return res.data;
    },

    async getStatus(): Promise<GoogleStatusResponse> {
        const res = await api.get<GoogleStatusResponse>('/integrations/google/status');
        return res.data;
    },

    async disconnect(): Promise<{ status: string }> {
        const res = await api.post<{ status: string }>('/integrations/google/disconnect');
        return res.data;
    },

    async getAccessToken(): Promise<{ access_token: string }> {
        const res = await api.get<{ access_token: string }>('/integrations/google/access-token');
        return res.data;
    },

    async importSheets(
        fileIds: string[],
        clientRequestId?: string,
        fileTitles?: Record<string, string>,
    ): Promise<ImportResponse> {
        // ``file_titles`` is optional on the backend; when present it
        // pre-populates the optimistic "Ingesting…" row so the user
        // sees the actual spreadsheet name while the worker runs
        // instead of a generic placeholder.
        const res = await api.post<ImportResponse>('/integrations/google-sheets/import', {
            file_ids: fileIds,
            client_request_id: clientRequestId,
            file_titles: fileTitles,
        });
        return res.data;
    },

    async resync(sourceDocumentId: string, clientRequestId?: string): Promise<ImportResponse> {
        const res = await api.post<ImportResponse>(
            `/integrations/google-sheets/resync/${encodeURIComponent(sourceDocumentId)}`,
            {},
            { params: clientRequestId ? { client_request_id: clientRequestId } : undefined }
        );
        return res.data;
    },

    async getJob(jobId: string): Promise<JobStatusResponse> {
        const res = await api.get<JobStatusResponse>(`/integrations/google-sheets/jobs/${encodeURIComponent(jobId)}`);
        return res.data;
    },

    /** Active-version structured data for agents / RAG tooling (read-only). */
    async getStructuredDocument(
        sourceDocumentId: string,
        options?: { maxRowsPerTable?: number }
    ): Promise<GoogleSheetsStructuredResponse> {
        const res = await api.get<GoogleSheetsStructuredResponse>(
            `/integrations/google-sheets/documents/${encodeURIComponent(sourceDocumentId)}/structured`,
            {
                params:
                    options?.maxRowsPerTable != null
                        ? { max_rows_per_table: options.maxRowsPerTable }
                        : undefined,
            }
        );
        return res.data;
    },

    /** Idempotent: same client_request_id returns the same outcome (replay-safe). */
    async sheetEditStop(conversationId: string, clientRequestId: string): Promise<{
        status: string;
        cancelled_pending?: number;
        message?: string;
    }> {
        const res = await api.post('/integrations/google-sheets/edit/stop', {
            conversation_id: conversationId,
            client_request_id: clientRequestId,
        });
        return res.data;
    },

    /** Restore cells from pre-apply snapshot (after approve). Idempotent per client_request_id. */
    async sheetEditRevert(proposalId: string, clientRequestId: string): Promise<{
        status: string;
        message?: string;
        already_reverted?: boolean;
        source_document_id?: string;
    }> {
        const res = await api.post('/integrations/google-sheets/edit/revert', {
            proposal_id: proposalId,
            client_request_id: clientRequestId,
        });
        return res.data;
    },

    /** Apply a pending sheet edit (Sheets API write). Idempotent per client_request_id. */
    async sheetEditApply(proposalId: string, clientRequestId: string): Promise<{
        status: string;
        message?: string;
        source_document_id?: string;
    }> {
        const res = await api.post('/integrations/google-sheets/edit/apply', {
            proposal_id: proposalId,
            client_request_id: clientRequestId,
        });
        return res.data;
    },

    /** Reject one pending proposal without writing to Sheets. Idempotent per client_request_id. */
    async sheetEditRejectProposal(proposalId: string, clientRequestId: string): Promise<{
        status: string;
        message?: string;
    }> {
        const res = await api.post('/integrations/google-sheets/edit/reject-proposal', {
            proposal_id: proposalId,
            client_request_id: clientRequestId,
        });
        return res.data;
    },

    /** Latest pending sheet edit for this CFO conversation (when message meta was missing). */
    async getPendingSheetEdit(conversationId: string): Promise<{
        proposal_id: string | null;
        pending: boolean;
    }> {
        const res = await api.get('/integrations/google-sheets/edit/pending', {
            params: { conversation_id: conversationId },
        });
        return res.data;
    },

    /**
     * Keep (confirm) a Google Sheet edit that was applied in the apply-then-confirm flow.
     * No Sheets API call; just dismisses the decision card. Idempotent per client_request_id.
     */
    async sheetEditConfirm(proposalId: string, clientRequestId: string): Promise<{
        status: string;
        message?: string;
        already_confirmed?: boolean;
        source_document_id?: string;
    }> {
        const res = await api.post('/integrations/google-sheets/edit/confirm', {
            proposal_id: proposalId,
            client_request_id: clientRequestId,
        });
        return res.data;
    },

    /** Latest applied-but-awaiting-confirmation sheet edit for this conversation (UI fallback). */
    async getAwaitingConfirmationSheetEdit(conversationId: string): Promise<{
        proposal_id: string | null;
        awaiting_confirmation: boolean;
        revert_available?: boolean;
        spreadsheet_title?: string;
    }> {
        const res = await api.get('/integrations/google-sheets/edit/awaiting-confirmation', {
            params: { conversation_id: conversationId },
        });
        return res.data;
    },

    // ==== Document-agent v2 ==============================================

    /**
     * Snapshot of the conversation's document-agent state: any parked
     * clarification, the latest aggregate awaiting-confirmation run, and the
     * last completed run's iteration summaries. Poll-friendly; the backend
     * expires pending clarifications via Mongo TTL so stale data self-cleans.
     */
    async getAgentState(conversationId: string): Promise<AgentStateResponse> {
        const res = await api.get<AgentStateResponse>(
            '/integrations/google-sheets/agent-state',
            { params: { conversation_id: conversationId } }
        );
        return res.data;
    },

    /**
     * Submit an answer to a parked clarification. Currently the frontend posts
     * the same text as the next chat turn so the orchestrator resumes via
     * consume_pending_clarification; this endpoint is also used for the
     * explicit-cancel path from the ClarificationCard.
     */
    async answerClarification(
        conversationId: string,
        answer: string,
        clientRequestId: string
    ): Promise<{ status: string; dismissed: boolean; message?: string }> {
        const res = await api.post(
            '/integrations/google-sheets/agent-state/clarify',
            {
                conversation_id: conversationId,
                answer,
                client_request_id: clientRequestId,
            }
        );
        return res.data;
    },

    /** Append-only phase trace — powers the AgentStatusStrip poll fallback. */
    async getAgentTrace(
        conversationId: string,
        limit: number = 50
    ): Promise<AgentTraceResponse> {
        const res = await api.get<AgentTraceResponse>(
            '/integrations/google-sheets/agent-trace',
            { params: { conversation_id: conversationId, limit } }
        );
        return res.data;
    },

    /** Approve an aggregate v2 run — flips every child proposal to confirmed. */
    async agentRunConfirm(
        runId: string,
        clientRequestId: string
    ): Promise<{ status: string; message?: string; already_confirmed?: boolean }> {
        const res = await api.post(
            '/integrations/google-sheets/agent-run/confirm',
            { run_id: runId, client_request_id: clientRequestId }
        );
        return res.data;
    },

    /** Reject an aggregate v2 run — LIFO revert every iteration + record lesson. */
    async agentRunRevert(
        runId: string,
        conversationId: string,
        clientRequestId: string,
        rejectReason: string = ''
    ): Promise<{ status: string; message?: string; reverted?: number }> {
        const res = await api.post(
            '/integrations/google-sheets/agent-run/revert',
            {
                run_id: runId,
                conversation_id: conversationId,
                client_request_id: clientRequestId,
                reject_reason: rejectReason,
            }
        );
        return res.data;
    },

    // ==== Per-sheet schema context (Context panel + read-path HITL) =====

    /**
     * Full schema_context for a source doc: description, per-tab schema,
     * audit history. Used by SheetContextPanel to render a light-grey
     * collapsible rail next to the embedded sheet. Tenant-scoped server-side.
     */
    async getSheetContext(sourceDocumentId: string): Promise<{
        source_document_id: string;
        schema_context: Record<string, unknown>;
    }> {
        const res = await api.get(
            `/integrations/google-sheets/documents/${encodeURIComponent(sourceDocumentId)}/context`
        );
        return res.data;
    },

    /**
     * Persist a user edit to one tab's schema context. Server flips the tab
     * to ``locked=true``/``source="user_edit"``/``confidence=0.99`` so
     * re-sync will not stomp the correction, and appends an audit history
     * entry. ``description`` is stored at the schema_context root.
     */
    async patchSheetContext(
        sourceDocumentId: string,
        body: {
            tab_name: string;
            header_rows?: number[];
            columns?: string[];
            data_start_row?: number;
            data_end_row?: number | null;
            notes?: string;
            description?: string;
            column_summaries?: { name: string; type: string; samples: unknown[] }[];
            locked?: boolean;
        }
    ): Promise<{
        source_document_id: string;
        schema_context: Record<string, unknown>;
    }> {
        const res = await api.patch(
            `/integrations/google-sheets/documents/${encodeURIComponent(sourceDocumentId)}/context`,
            body
        );
        return res.data;
    },

    /**
     * Soft-approve an auto-detected schema from the read-path clarification
     * panel. Server bumps confidence +0.10 (cap 0.99) and flips source to
     * ``user_confirmed_read`` but does NOT lock — locking still requires an
     * explicit edit-path confirm or a Context-panel save.
     */
    async confirmSheetContext(
        sourceDocumentId: string,
        tabName: string
    ): Promise<{
        source_document_id: string;
        tab_name: string;
        confidence: number;
        source: string;
    }> {
        const res = await api.post(
            `/integrations/google-sheets/documents/${encodeURIComponent(sourceDocumentId)}/context/confirm`,
            { tab_name: tabName }
        );
        return res.data;
    },
};

export default googleSheetsIntegrationApi;
